import { useEffect, useState } from 'react';
import { apiBase, authToken } from '../api/apiBase';

export type MackAcquisitionAddress = 'calendar.calendar' | 'inputs.macro_variables';
export interface MackAcquisitionRow {
  domain: 'TRADING'; agent: 'MACK'; semantic_address: MackAcquisitionAddress;
  worker_identity: 'trading.mack.codex' | 'trading.mack.claude';
  job_id: string; request_id: string; trace_id: string;
  record_id: string; record_sha256: string;
  subject: string; sheet: string; block: string;
  identity: string; period: string; vintage: string;
  publisher: string; source_url: string; source_registry_id: string;
  raw_value: number | null; unit: string | null; frequency: string;
  published_at: string | null; retrieved_at: string | null;
  scheduled_at: string | null; effective_at: string | null;
  excerpt: string; excerpt_sha256: string;
  admission_state: 'ADMITTED_RAW_EVIDENCE_NO_DOWNSTREAM';
}
export interface MackAcquisitionView {
  schema: 'mack_acquisition_view_v1'; domain: 'TRADING'; agent: 'MACK';
  semantic_address: MackAcquisitionAddress; rows: MackAcquisitionRow[];
  formula_execution: 'NOT_EXECUTED'; downstream_execution: false;
}
const obj = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
const text = (v: unknown): v is string => typeof v === 'string' && !!v.trim();
const hash = (v: unknown): v is string => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);

export function parseMackAcquisitionView(raw: unknown, address: MackAcquisitionAddress): MackAcquisitionView | null {
  const view = obj(raw);
  if (!view || view.success !== true || view.schema !== 'mack_acquisition_view_v1'
    || view.domain !== 'TRADING' || view.agent !== 'MACK' || view.semantic_address !== address
    || view.formula_execution !== 'NOT_EXECUTED' || view.downstream_execution !== false
    || !Array.isArray(view.rows)) return null;
  const seen = new Set<string>();
  for (const value of view.rows) {
    const row = obj(value);
    if (!row || row.domain !== 'TRADING' || row.agent !== 'MACK' || row.semantic_address !== address
      || !['trading.mack.codex', 'trading.mack.claude'].includes(String(row.worker_identity))
      || row.admission_state !== 'ADMITTED_RAW_EVIDENCE_NO_DOWNSTREAM'
      || !['record_id', 'job_id', 'request_id', 'trace_id', 'identity', 'period', 'vintage',
        'sheet', 'block', 'publisher', 'source_url', 'source_registry_id'].every(key => text(row[key]))
      || !hash(row.record_sha256) || !hash(row.excerpt_sha256)
      || typeof row.excerpt !== 'string' || row.excerpt.length > 1000
      || !(row.raw_value === null || (typeof row.raw_value === 'number' && Number.isFinite(row.raw_value)))
      || !(row.unit === null || typeof row.unit === 'string')) return null;
    if (seen.has(row.record_id as string)) return null;
    seen.add(row.record_id as string);
    try {
      const url = new URL(row.source_url as string);
      if (url.protocol !== 'https:' || url.username || url.password) return null;
    } catch { return null; }
  }
  return view as unknown as MackAcquisitionView;
}

export function validMackAcquisitionReceipt(raw: unknown, requestId: string, traceId: string,
  address: unknown, jobId: unknown): boolean {
  const receipt = obj(raw), readback = obj(receipt?.interface_readback);
  if (!receipt || !readback || receipt.status !== 'success' || receipt.memory_saved !== true
    || receipt.writer_calls !== 1 || receipt.rolled_back !== false
    || receipt.admission_state !== 'ADMITTED_RAW_EVIDENCE_NO_DOWNSTREAM'
    || receipt.t1_write !== false || receipt.t2_write !== false || receipt.formula_calls !== 0
    || receipt.pulse_activated !== false || !text(jobId) || receipt.job_id !== jobId
    || receipt.operation_id !== jobId || !text(receipt.record_id) || !hash(receipt.record_sha256)
    || readback.status !== 'PASS' || readback.projection !== 'mack_acquisition_view_v1'
    || readback.visible_row_count !== 1 || readback.record_id !== receipt.record_id
    || readback.record_sha256 !== receipt.record_sha256
    || !['calendar.calendar', 'inputs.macro_variables'].includes(String(address))) return false;
  const binding = { domain: 'TRADING', agent: 'MACK', request_id: requestId,
    trace_id: traceId, semantic_address: address, job_id: jobId };
  if (!Object.entries(binding).every(([key, value]) => receipt[key] === value && readback[key] === value)) return false;
  return ['trading.mack.codex', 'trading.mack.claude'].includes(String(receipt.worker_identity))
    && readback.worker_identity === receipt.worker_identity;
}

export async function fetchMackAcquisitionView(address: MackAcquisitionAddress, token: string,
  signal: AbortSignal): Promise<MackAcquisitionView> {
  const response = await fetch(`${apiBase()}/mack/acquisition-view?address=${encodeURIComponent(address)}`,
    { signal, cache: 'no-store', headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error('Acquisition view unavailable');
  const view = parseMackAcquisitionView(await response.json(), address);
  if (!view) throw new Error('Acquisition view could not be verified');
  return view;
}

export function useMackAcquisitionView(address: MackAcquisitionAddress, suppliedToken?: string | null) {
  const [view, setView] = useState<MackAcquisitionView | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const token = suppliedToken || authToken();
  useEffect(() => {
    let current: AbortController | null = null;
    let active = true;
    const load = () => {
      current?.abort();
      const controller = new AbortController(); current = controller;
      setView(null); setState('loading');
      fetchMackAcquisitionView(address, token, controller.signal).then(next => {
        if (active && !controller.signal.aborted) { setView(next); setState('ready'); }
      }).catch(() => {
        if (active && !controller.signal.aborted) { setView(null); setState('unavailable'); }
      });
    };
    load();
    const changed = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.domain === 'TRADING' && detail?.agent === 'MACK' && detail?.semantic_address === address) load();
    };
    window.addEventListener('mack-acquisition-updated', changed);
    return () => { active = false; current?.abort(); window.removeEventListener('mack-acquisition-updated', changed); };
  }, [address, token]);
  return { view, state };
}
